/**
 * Product Zoom Hi-Res
 *
 * The product gallery zooms by scaling the displayed image (product-media-zoom.js), which is only
 * loaded at display size, so the zoomed view looked blurry. Zoomable images carry a high-res
 * source in data-zoom-src (snippets/image.liquid); this swaps it in when the pointer first enters
 * the image (desktop) or on first tap, so the zoom magnifies a sharp image.
 *
 * Standalone (no imports) so it doesn't depend on the minified bundle.
 */
(() => {
  if (window.__productZoomHiRes) return;
  window.__productZoomHiRes = true;

  const upgrade = (img) => {
    if (!img || img.dataset.zoomUpgraded) return;
    const hiRes = img.dataset.zoomSrc;
    if (!hiRes) return;
    // Wait for the normal lazy load to finish, so the lazy loader doesn't overwrite our source
    if (img.hasAttribute('data-lazy-image') && !img.classList.contains('loaded')) return;

    img.dataset.zoomUpgraded = 'loading';
    const preload = new Image();
    preload.onload = () => {
      img.removeAttribute('srcset');
      img.removeAttribute('data-srcset');
      img.src = hiRes;
      img.dataset.zoomUpgraded = 'done';
    };
    preload.onerror = () => {
      delete img.dataset.zoomUpgraded; // keep the normal image; try again next time
    };
    preload.src = hiRes;
  };

  const zoomImage = (event) => event.target.closest && event.target.closest('img[data-zoom-src]');

  // Most product photos are only ~1000px wide, so a fixed 2x zoom would stretch them.
  // Zoom only as far as the original photo allows (max 2x); skip zoom when it can't add detail.
  const MAX_SCALE = 2;
  const MIN_USEFUL_SCALE = 1.15;
  const maxScaleFor = (img) => {
    const originalW = Number(img.dataset.zoomWidth) || 0;
    const originalH = Number(img.dataset.zoomHeight) || 0;
    // Layout box, unaffected by the zoom transform
    const boxW = img.offsetWidth;
    const boxH = img.offsetHeight;
    if (!originalW || !boxW) return MAX_SCALE;
    if (!originalH || !boxH) return Math.min(MAX_SCALE, originalW / boxW);
    // How much the photo is currently scaled to fit its box ('contain' fits inside, 'cover' fills)
    const fitW = boxW / originalW;
    const fitH = boxH / originalH;
    const shownScale = getComputedStyle(img).objectFit === 'cover' ? Math.max(fitW, fitH) : Math.min(fitW, fitH);
    // Zooming further than 1/shownScale would enlarge the photo past its real resolution
    return Math.min(MAX_SCALE, 1 / shownScale);
  };
  const canZoom = (img) => maxScaleFor(img) >= MIN_USEFUL_SCALE;

  // Desktop: start loading as soon as the pointer is over the image, before the click
  document.addEventListener('pointerover', (event) => {
    const img = zoomImage(event);
    if (!img) return;
    if (!canZoom(img)) img.style.cursor = 'default';
    if (event.pointerType === 'mouse') upgrade(img);
  }, { passive: true });

  // Touch / keyboard / anything else: load on the first tap or click
  document.addEventListener('click', (event) => upgrade(zoomImage(event)), true);

  // Runs after product-media-zoom.js has applied its fixed scale: cap it to what the photo supports
  document.addEventListener('click', (event) => {
    const img = zoomImage(event);
    if (!img || !/scale\((?!1\))/.test(img.style.transform)) return; // only when zooming in
    if (!canZoom(img)) {
      img.style.transform = 'scale(1)';
      img.style.cursor = 'default';
      return;
    }
    img.style.transform = `scale(${maxScaleFor(img).toFixed(2)})`;
  });
})();
